import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface PurchaseFormProps {
  variantId: number;
  companySchema: string;
  localDestinoId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface Supplier {
  id: number;
  nombre: string;
}

interface CashRegister {
  id: number;
  nombre: string;
}

export const PurchaseForm: React.FC<PurchaseFormProps> = ({
  variantId,
  companySchema,
  localDestinoId,
  onClose,
  onSuccess
}) => {
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [cashRegisterId, setCashRegisterId] = useState('');
  const [shippingDays, setShippingDays] = useState('0');
  const [paymentDays, setPaymentDays] = useState('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [cashRegisters, setCashRegisters] = useState<CashRegister[]>([]);
  const [productName, setProductName] = useState<string>('');

  React.useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Fetch product name
      const { data: variantData, error: variantError } = await supabase
        .schema(companySchema)
        .from('variantes')
        .select(`
          nombre,
          productos (
            nombre
          )
        `)
        .eq('id', variantId)
        .single();

      if (variantError) throw variantError;
      if (variantData) {
        setProductName(`${variantData.productos.nombre} - ${variantData.nombre}`);
      }

      // Fetch suppliers
      const { data: suppliersData, error: suppliersError } = await supabase
        .schema(companySchema)
        .from('proveedores')
        .select('id, nombre')
        .order('nombre');

      if (suppliersError) throw suppliersError;
      setSuppliers(suppliersData || []);

      // Fetch cash registers
      const { data: cashRegistersData, error: cashRegistersError } = await supabase
        .schema(companySchema)
        .from('cajas')
        .select('id, nombre')
        .order('nombre');

      if (cashRegistersError) throw cashRegistersError;
      setCashRegisters(cashRegistersData || []);

    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError(err instanceof Error ? err.message : 'Error loading data');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      if (!localDestinoId) {
        throw new Error('No se ha seleccionado un depósito destino');
      }

      const currentDate = new Date();
      const deliveryDate = new Date(currentDate);
      deliveryDate.setDate(deliveryDate.getDate() + parseInt(shippingDays));

      const paymentDate = new Date(deliveryDate);
      paymentDate.setDate(paymentDate.getDate() + parseInt(paymentDays));

      const totalAmount = parseFloat(quantity) * parseFloat(unitPrice);

      // Start a Supabase transaction
      const { data: purchase, error: purchaseError } = await supabase
        .schema(companySchema)
        .from('compras')
        .insert([{
          variante_id: variantId,
          cantidad_comprada: parseInt(quantity),
          costo_unitario: parseFloat(unitPrice),
          fecha_compra: currentDate.toISOString(),
          local_destino_id: localDestinoId,
          proveedores_id: parseInt(supplierId),
          caja_id: parseInt(cashRegisterId),
          estado: shippingDays === '0' ? 'recibida' : 'en transito',
          fecha_estimada_entrega: deliveryDate.toISOString(),
          user_id: user.id
        }])
        .select()
        .single();

      if (purchaseError) throw purchaseError;

      // Create cash movement
      const { error: movementError } = await supabase
        .schema(companySchema)
        .from('movimientos_caja')
        .insert([{
          caja_id: parseInt(cashRegisterId),
          tipo_movimiento: 'Egreso',
          monto: totalAmount,
          descripcion: `Compra de ${quantity} unidades de ${productName}`,
          fecha: paymentDate.toISOString(),
          usuario_id: user.id
        }]);

      if (movementError) throw movementError;

      // If immediate delivery, update stock
      if (shippingDays === '0') {
        // First get the current stock value
        const { data: currentStock, error: stockFetchError } = await supabase
          .schema(companySchema)
          .from('stock')
          .select('cantidad_disponible')
          .eq('variantes_id', variantId)
          .eq('local_id', localDestinoId)
          .single();

        if (stockFetchError) throw stockFetchError;

        // Then update with the new calculated value
        const { error: stockError } = await supabase
          .schema(companySchema)
          .from('stock')
          .update({
            cantidad_disponible: (currentStock?.cantidad_disponible || 0) + parseInt(quantity)
          })
          .eq('variantes_id', variantId)
          .eq('local_id', localDestinoId);

        if (stockError) throw stockError;
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error registering purchase:', err);
      setError(err instanceof Error ? err.message : 'Error registering purchase');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-h-[80vh] overflow-y-auto">
      <h3 className="text-lg font-medium text-gray-900 mb-6">
        Registrar nueva compra
      </h3>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cantidad
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio por unidad
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proveedor
            </label>
            <select
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Seleccionar proveedor</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tiempo de envío
            </label>
            <select
              value={shippingDays}
              onChange={(e) => setShippingDays(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="0">Inmediato</option>
              <option value="1">1 día</option>
              <option value="2">2 días</option>
              <option value="3">3 días</option>
              <option value="5">5 días</option>
              <option value="7">7 días</option>
              <option value="14">14 días</option>
              <option value="30">30 días</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Caja
            </label>
            <select
              value={cashRegisterId}
              onChange={(e) => setCashRegisterId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Seleccionar caja</option>
              {cashRegisters.map(register => (
                <option key={register.id} value={register.id}>
                  {register.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plazo de pago
            </label>
            <select
              value={paymentDays}
              onChange={(e) => setPaymentDays(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="0">Inmediato</option>
              <option value="15">15 días</option>
              <option value="30">30 días</option>
              <option value="45">45 días</option>
              <option value="60">60 días</option>
              <option value="90">90 días</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Registrando...
              </>
            ) : (
              'Registrar compra'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};